/**
 * One-click "Download PDF" for the WeKIT™ Career Clarity 360 Passport.
 *
 * Uses the same sheets the print stylesheet paginates: every `.dossier-sheet`
 * becomes one A4 page, captured at 2x and placed with a small margin. Going
 * through the existing layout rather than a second PDF renderer means the
 * downloaded file and the printed page can never disagree.
 */
import { ONET_ATTRIBUTION_DOCUMENT_SENTENCE } from "@/lib/onetAttribution";

const A4_W = 210;
const A4_H = 297;
const MARGIN = 8;

export async function downloadDossierPdf(fileName: string): Promise<void> {
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
    import("html2canvas"),
    import("jspdf"),
  ]);

  const sheets = Array.from(document.querySelectorAll<HTMLElement>(".dossier-sheet"));
  if (sheets.length === 0) throw new Error("Nothing to export yet.");

  const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const surface = getComputedStyle(document.body).backgroundColor || "#ffffff";
  const maxW = A4_W - MARGIN * 2;
  const maxH = A4_H - MARGIN * 2;

  for (let i = 0; i < sheets.length; i++) {
    const canvas = await html2canvas(sheets[i], {
      scale: 2,
      useCORS: true,
      backgroundColor: surface,
      logging: false,
      windowWidth: sheets[i].scrollWidth,
    });

    // Fit inside the printable box, preserving aspect ratio.
    const ratio = canvas.height / canvas.width;
    let w = maxW;
    let h = w * ratio;
    if (h > maxH) {
      h = maxH;
      w = h / ratio;
    }

    if (i > 0) pdf.addPage();
    pdf.addImage(
      canvas.toDataURL("image/jpeg", 0.92),
      "JPEG",
      MARGIN + (maxW - w) / 2,
      MARGIN,
      w,
      h,
    );

    // Licence condition: the O*NET attribution must appear on the exported
    // file, not only on screen. Printed once per page, below the sheet image.
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(6);
    pdf.setTextColor(110, 110, 110);
    const notice = pdf.splitTextToSize(ONET_ATTRIBUTION_DOCUMENT_SENTENCE, maxW) as string[];
    let noticeY = A4_H - MARGIN - notice.length * 2.4;
    for (const lineText of notice) {
      pdf.text(lineText, MARGIN, noticeY);
      noticeY += 2.4;
    }
  }

  pdf.save(fileName.endsWith(".pdf") ? fileName : `${fileName}.pdf`);
}

/** `WeKIT-Passport-Asha-2026-08-02.pdf` — safe on every filesystem. */
export function dossierFileName(learnerName: string): string {
  const slug = learnerName
    .normalize("NFKD")
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 40) || "Report";
  const date = new Date().toISOString().slice(0, 10);
  return `WeKIT-Passport-${slug}-${date}.pdf`;
}
